import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import PublicLayout from '@/layouts/PublicLayout';
import PrivateLayout from '@/layouts/PrivateLayout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import AdminConsolePage from '@/pages/AdminConsolePage';
import InsightsPage from '@/pages/InsightsPage';
import RSOCKeywordsPage from '@/pages/RSOCKeywordsPage';
import CompliancePage from '@/pages/CompliancePage';
import StudioPage from '@/pages/StudioPage';
import MediaLibraryPage from '@/pages/MediaLibraryPage';
import AdsManagerPage from '@/pages/AdsManagerPage';
import DevelopersPage from '@/pages/DevelopersPage';

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

function App() {
  const { checkAuth, isAuthenticated } = useAuthStore();
  const authCheckedRef = useRef(false);

  useEffect(() => {
    // Check authentication status on app load
    // Use ref to prevent duplicate calls in React StrictMode (development)
    if (!authCheckedRef.current) {
      authCheckedRef.current = true;
      checkAuth();
    }
  }, [checkAuth]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<PublicLayout />}>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
        />
      </Route>

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <PrivateLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/rsoc-keywords" element={<RSOCKeywordsPage />} />
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/studio" element={<StudioPage />} />
        <Route path="/media-library" element={<MediaLibraryPage />} />
        <Route path="/ads-manager" element={<AdsManagerPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/admin" element={<AdminConsolePage />} />
        {/* Add more protected routes here */}
      </Route>

      {/* Catch all - redirect to dashboard or login */}
      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />}
      />
    </Routes>
  );
}

export default App;
